import { useState, useEffect } from 'react';
import { Check, Crown, Zap, Star, ArrowRight, CreditCard, Smartphone } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PricingSection({ onSelectPackage }) {
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await axios.get(`${API}/payment/packages`);
        setPackages(response.data.packages);
      } catch (error) {
        console.error('Paketler yüklenemedi:', error);
        // Fallback packages
        setPackages({
          "3_days": { amount: 199, currency: "SEK", days: 3, name: "3 Gün Premium" },
          "15_days": { amount: 349, currency: "SEK", days: 15, name: "15 Gün Premium" },
          "1_month": { amount: 550, currency: "SEK", days: 30, name: "1 Ay Premium" },
          "2_months": { amount: 750, currency: "SEK", days: 60, name: "2 Ay Premium" },
          "3_months": { amount: 1100, currency: "SEK", days: 90, name: "3 Ay Premium" }
        });
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, []);

  const packageOrder = ['3_days', '15_days', '1_month', '2_months', '3_months'];
  const sortedPackages = packageOrder.map(key => ({ id: key, ...packages[key] })).filter(p => p.name);

  // Highlight the most popular package (1 month)
  const popularPackage = '1_month';

  const handleSelectPackage = (pkgId) => {
    if (onSelectPackage) {
      onSelectPackage(pkgId);
    } else {
      // Navigate to payment page
      window.location.href = '/payment';
    }
  };

  if (loading) {
    return (
      <div className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-spin w-10 h-10 border-4 border-red-500/30 border-t-red-500 rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <section id="pricing" className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white" data-testid="pricing-section">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 text-red-600 rounded-full text-sm font-semibold mb-4">
            <CreditCard className="w-4 h-4" />
            <span>Güvenli Ödeme Yöntemleri</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4">
            Premium Paketler
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            Tam erişim ile sınavınıza en iyi şekilde hazırlanın
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6 max-w-7xl mx-auto">
          {sortedPackages.map((pkg, index) => {
            const isPopular = pkg.id === popularPackage;
            const dailyPrice = (pkg.amount / pkg.days).toFixed(0);
            
            return (
              <div
                key={pkg.id}
                className={`relative rounded-2xl p-6 transition-all duration-300 hover:scale-105 cursor-pointer ${
                  isPopular 
                    ? 'bg-gradient-to-b from-red-600 to-red-700 text-white shadow-2xl shadow-red-500/30 ring-4 ring-red-500/50 z-10 transform scale-105' 
                    : 'bg-white text-gray-900 shadow-lg hover:shadow-xl border border-gray-200'
                }`}
                onClick={() => handleSelectPackage(pkg.id)}
                data-testid={`pricing-card-${pkg.id}`}
              >
                {/* Popular Badge */}
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-yellow-400 text-yellow-900 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
                    <Star className="w-3 h-3" fill="currentColor" />
                    EN POPÜLER
                  </div>
                )}

                {/* Package Name */}
                <div className="text-center mb-4">
                  <h3 className={`text-xl font-bold mb-1 ${isPopular ? 'text-white' : 'text-gray-900'}`}>
                    {pkg.name}
                  </h3>
                  <p className={`text-sm ${isPopular ? 'text-red-200' : 'text-gray-500'}`}>
                    {pkg.days} gün tam erişim
                  </p>
                </div>

                {/* Price */}
                <div className="text-center mb-6">
                  <div className={`text-4xl md:text-5xl font-black ${isPopular ? 'text-white' : 'text-gray-900'}`}>
                    {pkg.amount}
                    <span className={`text-lg font-normal ${isPopular ? 'text-red-200' : 'text-gray-500'}`}> {pkg.currency}</span>
                  </div>
                  <p className={`text-sm mt-1 ${isPopular ? 'text-red-200' : 'text-gray-500'}`}>
                    ~{dailyPrice} SEK/gün
                  </p>
                </div>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {[
                    '2000+ Güncel Soru',
                    'Detaylı Açıklamalar',
                    'Sınav Modu',
                    'İlerleme Takibi'
                  ].map((feature, i) => (
                    <li key={i} className={`flex items-center gap-2 text-sm ${isPopular ? 'text-red-100' : 'text-gray-600'}`}>
                      <Check className={`w-4 h-4 ${isPopular ? 'text-yellow-400' : 'text-green-500'}`} />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <button
                  className={`w-full py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                    isPopular 
                      ? 'bg-white text-red-600 hover:bg-red-50' 
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  <span>Satın Al</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Payment Methods */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm mb-4">Güvenli Ödeme Yöntemleri</p>
          <div className="flex justify-center items-center gap-6 flex-wrap">
            <div className="flex items-center gap-2 text-gray-600">
              <CreditCard className="w-6 h-6" />
              <span className="text-sm font-medium">Kredi Kartı</span>
            </div>
            <div className="flex items-center gap-2 text-blue-600">
              <Smartphone className="w-6 h-6" />
              <span className="text-sm font-medium">Swish</span>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-8 flex flex-wrap justify-center gap-6 text-gray-400 text-sm">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-500" />
            <span>Anında Erişim</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-500" />
            <span>7/24 Destek</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-500" />
            <span>SSL Şifreli</span>
          </div>
        </div>
      </div>
    </section>
  );
}
